import type { Recurrence, TimeInterval, Weekday } from 'typings'

const CRON_REGEX = /\* \* (?<cronTimeRanges>.+) \* \* (?<cronDays>.+)$/

export const CRON_WEEKDAYS: { [key in Weekday]: string } = {
  monday: 'MON',
  tuesday: 'TUE',
  wednesday: 'WED',
  thursday: 'THU',
  friday: 'FRI',
  saturday: 'SAT',
  sunday: 'SUN',
}

const MIN_IN_MS = 60 * 1000

/**
 * @param {Date} date a date object to which the offset will be applied
 * @param {number} offset timezone offset to be applied (e.g. -3)
 * @returns a new date object with the offset applied
 */
function addOffset(date: Date, offset: number): Date {
  const currentOffset = new Date().getTimezoneOffset()
  return new Date(date.getTime() - (currentOffset + offset * 60) * MIN_IN_MS)
}

/**
 * @param {string} range a cron time range expression (e.g. 10-11). Ranges express full intervals, meaning that 10-11 represents the interval from 10:00 to 11:59
 * @param {number} offset timezone offset to be applied to the parsed time range. A user in UTC timezone would see the interval 10-11 with an offset of -3 as 07-08 (07:00 - 08:59)
 * @returns {TimeInterval} the parsed time range
 */
function parseTimeRange(range: string, offset: number): TimeInterval {
  const [expressionStartTime, expressionEndTime] = range.split('-')

  let start = new Date(0)
  let end = new Date(0)

  start.setHours(Number(expressionStartTime))
  end.setHours(Number(expressionEndTime) + 1)

  start = addOffset(start, offset)
  end = addOffset(end, offset)

  if (
    !(start instanceof Date && window.isFinite(start.getTime())) ||
    !(end instanceof Date && window.isFinite(end.getTime()))
  ) {
    throw new Error(`Error parsing recurrence time range: ${range}`)
  }

  return { start, end }
}

/**
 * @param {string | undefined} intervals a set of intervals in cron format (e.g. 10-11,14-15)
 * @param {number} offset timezone offset to be applied to the parsed time range.
 * @returns {TimeInterval[]} the parsed time ranges
 */
function parseTimeIntervals(
  intervals: string | undefined,
  offset: number
): TimeInterval[] {
  let timeIntervals: TimeInterval[] = []

  if (intervals !== '*') {
    timeIntervals =
      intervals
        ?.split(',')
        .map((interval) => parseTimeRange(interval, offset)) ?? []
  }

  return timeIntervals
}

/**
 * @param {string} cronWeekdays a cron weekdays expression (e.g. MON,WED,FRI)
 * @returns {Weekday[]} the parsed values as an array of Weekdays (e.g. ['monday', 'wednesday', 'friday'])
 */
function parseWeekdays(cronWeekdays: string | undefined): Weekday[] {
  const cronDays = cronWeekdays?.trim().split(',')

  type WeekdayKey = keyof typeof CRON_WEEKDAYS

  return Object.keys(CRON_WEEKDAYS).filter((x) =>
    cronDays?.includes(CRON_WEEKDAYS[x as WeekdayKey])
  ) as WeekdayKey[]
}

/**
 * @param cronExpression a [cron expression](https://github.com/HangfireIO/Cronos?tab=readme-ov-file#cron-format) representing the recurrence settings (e.g. * * 10-11 * * MON,WED,FRI)
 * @param {number} offset timezone offset to be applied to the parsed time range. A user in UTC timzone would see the interval 10-11 with an offset of -3 as 07:00 - 08:59
 * @returns
 */
export function parseCron(
  cronExpression: string | undefined,
  offset: number
): Recurrence | undefined {
  try {
    const match = cronExpression?.match(CRON_REGEX)

    if (!match) {
      return undefined
    }

    const { groups } = match

    const weekdays = parseWeekdays(groups?.cronDays)
    const timeIntervals = parseTimeIntervals(groups?.cronTimeRanges, offset)

    return {
      weekdays,
      timeIntervals,
    }
  } catch (error) {
    console.error('Error parsing recurrence settings', error)

    return undefined
  }
}

/**
 * @param {Recurrence} recurrence a recurrence object representing the promotion's recurrence settings
 * @returns {string} a [cron expression](https://github.com/HangfireIO/Cronos?tab=readme-ov-file#cron-format) representing the recurrence settings (e.g. * * 10-11 * * MON,WED,FRI)
 */
export function toCronExpression(recurrence: Recurrence): string {
  const cronDays = recurrence.weekdays
    .map((weekday) => CRON_WEEKDAYS[weekday])
    .join(',')

  const cronTimeRanges = recurrence.timeIntervals
    .filter(
      (interval): interval is Required<TimeInterval> =>
        !!interval.start && !!interval.end
    )
    .map(
      (interval) =>
        `${interval.start.getHours()}-${
          interval.end.getHours() - 1 < 0
            ? interval.end.getHours() + 23
            : interval.end.getHours() - 1
        }`
    )
    .join(',')

  return `* * ${cronTimeRanges || '*'} * * ${cronDays || '*'}`
}
